import { formatDistanceToNowStrict } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { Loader2, Rocket } from "lucide-react"
import { useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import type { CustomBot } from "@/entities/bot-builder"
import { plural } from "@/entities/audience"
import { PublishDialog, usePendingChanges } from "@/features/publish-scenario"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { botPath, type ScenarioDraftState } from "@/widgets/bot-workspace"

const STALE_MS = 24 * 60 * 60 * 1000

/**
 * Статус публикации и «Опубликовать»: справа в шапке раздела бота и редактора воронки.
 * Пока всё опубликовано, кнопка тихая; есть правки: видно сколько, а если последняя
 * публикация была давно, статус подсвечен, чтобы правки не забыли выкатить.
 */
export function PublishButton({
	bot,
	draft,
	compact = false,
}: {
	bot: CustomBot
	draft: ScenarioDraftState
	/** Без длинной подписи: статус виден в подсказке, а на кнопке только число правок. */
	compact?: boolean
}) {
	const navigate = useNavigate()
	const [open, setOpen] = useState(false)
	const pending = usePendingChanges(draft.draft, draft.isSaving)
	// Пока идёт сохранение число неизвестно (null): показываем прежнее, чтобы бейдж не мигал.
	const lastPending = useRef(0)
	if (pending !== null) lastPending.current = pending
	const shownPending = pending ?? lastPending.current
	if (!draft.draft) return null
	const published = draft.draft.latestVersion
	const dirty = !published || shownPending > 0
	const stale =
		dirty && !!published && Date.now() - new Date(published.publishedAt).getTime() > STALE_MS
	const status = draft.isSaving
		? "сохраняем…"
		: !published
			? "бот ещё не опубликован"
			: pending === null
				? `опубликована v${published.number}`
				: pending === 0
					? `всё опубликовано, v${published.number}`
					: `не опубликовано: ${pending} ${plural(pending, ["правка", "правки", "правок"])}${
							stale
								? `, с прошлой публикации ${formatDistanceToNowStrict(new Date(published.publishedAt), { locale: ru })}`
								: ""
						}`
	return (
		<>
			<span
				className={cn(
					"hidden items-center gap-1 text-xs",
					compact ? "hidden" : "lg:inline-flex",
					stale ? "text-amber-300" : "text-muted-foreground",
				)}
			>
				{draft.isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
				{status}
			</span>
			<Button
				size="sm"
				variant={dirty ? "default" : "outline"}
				title={compact ? status : dirty ? undefined : "Правок после последней публикации нет"}
				className="relative h-9 rounded-xl"
				onClick={() =>
					void draft
						.whenSaved()
						.then(() => setOpen(true))
						.catch((error: Error) => toast.error(error.message))
				}
			>
				<Rocket />
				<span className="hidden sm:inline">Опубликовать</span>
				{/* Бейдж поверх кнопки: ширина кнопки не меняется, соседи слева не сдвигаются. */}
				{compact && shownPending > 0 ? (
					<span className="absolute -top-1.5 -right-1.5 grid min-w-5 place-items-center rounded-full bg-foreground px-1 text-[11px] leading-5 font-semibold text-background tabular-nums">
						{shownPending}
					</span>
				) : null}
			</Button>
			<PublishDialog
				open={open}
				onOpenChange={setOpen}
				draft={draft.draft}
				isSaving={draft.isSaving}
				onPickNode={(nodeId) => {
					const node = draft.draft?.nodes.find((item) => item.id === nodeId)
					setOpen(false)
					if (node)
						navigate(botPath(bot.id, `constructor/funnels/${node.flowId}`), {
							state: { focusNodeId: nodeId },
						})
				}}
			/>
		</>
	)
}
